import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

const SHANGHAI_TZ = "Asia/Shanghai";

// 设置 dayjs.tz 的默认时区为东八区
dayjs.tz.setDefault(SHANGHAI_TZ);

/**
 * 将本地日期值转换为上海时间对应的时间戳（秒）
 * 无论本地时区如何，都将用户选择的“墙上时间”视为上海时间
 * @param date 日期值（可以是 Date 对象、dayjs 对象、字符串或时间戳）
 * @param type 'start' | 'end' | 'none'
 * @returns 对应的上海时间 10 位时间戳
 */
export const toShanghaiUnix = (
  date: any,
  type: "start" | "end" | "none" = "none",
) => {
  if (!date) return undefined;

  // 使用 format('YYYY-MM-DD HH:mm:ss') 把本地显示的墙钟时间直接当作上海时间
  // 这样可以规避本地时区的影响，强制以东八区处理
  let dj = dayjs.tz(dayjs(date).format("YYYY-MM-DD HH:mm:ss"), SHANGHAI_TZ);

  if (type === "start") {
    dj = dj.startOf("day");
  } else if (type === "end") {
    dj = dj.endOf("day");
  }

  return dj.unix();
};

/**
 * 获取上海时间的当前日期范围（秒级时间戳）
 */
export const getShanghaiRange = (
  offset = 0,
  unit: dayjs.ManipulateType = "day",
) => {
  const base = dayjs.tz().add(offset, unit);
  return [base.startOf(unit).unix(), base.endOf(unit).unix()];
};

/**
 * 格式化 10 位时间戳为上海时间字符串
 */
export const formatShanghai = (
  unix?: number,
  format = "YYYY-MM-DD HH:mm:ss",
) => {
  if (!unix) return "-";
  // dayjs.unix(unix) 默认是 UTC 时间转本地时间，加上 .tz() 转为上海时间
  return dayjs.unix(unix).tz().format(format);
};

// 导出包装后的 dayjs，使其默认使用上海时区
const dayjsWrapper: any = (
  date?: dayjs.ConfigType,
  format?: string,
  strict?: boolean,
) => {
  // 处理空输入
  if (date === undefined || date === null) {
    return dayjs.tz();
  }

  // 如果有 format，直接按上海时区解析
  if (typeof format === "string") {
    // dayjs.tz(date, format, timezone)
    return dayjs.tz(date, format, SHANGHAI_TZ);
  }

  // 对于数字类型（Unix 时间戳），通常代表一个绝对时间点，直接转为上海时区展示
  if (typeof date === "number") {
    // dayjs.tz(number, timezone)
    return dayjs.tz(date, SHANGHAI_TZ);
  }

  // 对于 Date 对象或不带 format 的字符串，为了满足“用户选的时间就认为是东八区”
  // 我们强制将其“墙上显示值”视为上海时间，规避本地时区干扰
  return dayjs.tz(dayjs(date).format("YYYY-MM-DD HH:mm:ss"), SHANGHAI_TZ);
};

// 复制 dayjs 的静态方法
Object.assign(dayjsWrapper, dayjs);

// 特殊处理一些工厂方法
dayjsWrapper.unix = (u: number) => dayjs.unix(u).tz();

export default dayjsWrapper as typeof dayjs;
